@if (state === EAiComparisonState.FAILED) {
  <div class="ai-failure" role="alertdialog" aria-labelledby="ai-failure-title">
    <div class="ai-failure__body">
      <span class="ai-failure__icon" aria-hidden="true">
        <mat-icon>warning</mat-icon>
      </span>
      <h2 class="ai-failure__title" id="ai-failure-title">
        {{ 'activity.ai_adjustment.failure.title' | translate }}
      </h2>
      <p class="ai-failure__message">{{ failureMessageKey | translate }}</p>
      @if (failureCategoryKey !== null) {
        <p class="ai-failure__category">{{ failureCategoryKey | translate }}</p>
      }
      @if (failureIsRetryable) {
        <p class="ai-failure__retry">{{ 'activity.ai_adjustment.failure.retry_hint' | translate }}</p>
      }
      @if (failureReference !== null) {
        <p class="ai-failure__reference">
          {{ 'activity.ai_adjustment.failure.reference' | translate: { reference: failureReference } }}
        </p>
      }
    </div>
    <div class="ai-failure__actions">
      <button mat-flat-button color="primary" type="button" (click)="closeFailure()">
        {{ 'generic.close' | translate }}
      </button>
    </div>
  </div>
} @else {
  @let isMobile = (showMobileLayout$ | async) === true;

  <div class="ai-compare" role="dialog" aria-labelledby="ai-compare-title">
    <div class="ai-compare__header">
      <span class="ai-compare__badge" aria-hidden="true">
        <mat-icon>auto_awesome</mat-icon>
      </span>
      <h2 class="ai-compare__title" id="ai-compare-title">
        {{ 'activity.ai_adjustment.compare.title' | translate }}
      </h2>

      @if (state === EAiComparisonState.GENERATING) {
        <span class="ai-compare__generating" aria-live="polite">
          <mat-spinner diameter="12"></mat-spinner>
          {{ 'activity.ai_adjustment.compare.generating' | translate }}
        </span>
      } @else {
        <span class="ai-compare__changed">
          {{ 'activity.ai_adjustment.compare.changed_count' | translate: { count: changedCount } }}
        </span>
        <button
          mat-icon-button
          type="button"
          class="ai-compare__close"
          [attr.aria-label]="'generic.close' | translate"
          [disabled]="discarding"
          (click)="discard()"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>

    @if (isMobile) {
      <div class="ai-compare__tabs">
        <button
          type="button"
          [class.ai-compare__tab--active]="comparisonTab === 'old'"
          [attr.aria-pressed]="comparisonTab === 'old'"
          class="ai-compare__tab"
          (click)="setComparisonTab('old')"
        >
          {{ 'activity.ai_adjustment.compare.old_version' | translate }}
        </button>
        <button
          type="button"
          [class.ai-compare__tab--active]="comparisonTab === 'new'"
          [attr.aria-pressed]="comparisonTab === 'new'"
          class="ai-compare__tab"
          (click)="setComparisonTab('new')"
        >
          {{ 'activity.ai_adjustment.compare.new_version' | translate }}
        </button>
      </div>
    } @else {
      <div class="ai-compare__columns">
        <span class="ai-compare__column-label">
          <span class="ai-compare__dot ai-compare__dot--old"></span>
          {{ 'activity.ai_adjustment.compare.old_version' | translate }}
        </span>
        <span class="ai-compare__column-label">
          <span class="ai-compare__dot ai-compare__dot--new"></span>
          {{ 'activity.ai_adjustment.compare.new_version' | translate }}
        </span>
      </div>
    }

    <div class="ai-compare__rows">
      @for (row of rows; track trackByRowKey($index, row)) {
        <div class="ai-compare__row" [class.ai-compare__row--single]="isMobile">
          @if (!isMobile || comparisonTab === 'old') {
            <div class="ai-compare__cell">
              <p class="ai-compare__label">
                {{ row.labelKey ? (row.labelKey | translate) : row.labelFallback }}
              </p>
              <div class="ai-compare__value" [class.ai-compare__value--old]="row.kind !== EAiComparisonRowKind.CONTEXT">
                <ng-container [ngTemplateOutlet]="oldValue" [ngTemplateOutletContext]="{ row: row }"></ng-container>
              </div>
              @if (row.changed && isMobile) {
                <span class="ai-compare__marker ai-compare__marker--old">
                  {{ 'activity.ai_adjustment.compare.marker.old' | translate }}
                </span>
              }
            </div>
          }

          @if (!isMobile || comparisonTab === 'new') {
            <div class="ai-compare__cell">
              <p class="ai-compare__label">
                {{ row.labelKey ? (row.labelKey | translate) : row.labelFallback }}
              </p>
              <div
                class="ai-compare__value"
                [class.ai-compare__value--new]="row.kind !== EAiComparisonRowKind.CONTEXT"
                [class.ai-compare__value--generating]="state === EAiComparisonState.GENERATING && row.kind !== EAiComparisonRowKind.CONTEXT"
              >
                @if (state === EAiComparisonState.GENERATING && row.kind !== EAiComparisonRowKind.CONTEXT) {
                  <mat-spinner diameter="16"></mat-spinner>
                } @else {
                  <ng-container [ngTemplateOutlet]="newValue" [ngTemplateOutletContext]="{ row: row }"></ng-container>
                }
              </div>
              @if (row.changed && state !== EAiComparisonState.GENERATING) {
                <span class="ai-compare__marker ai-compare__marker--new">
                  {{ 'activity.ai_adjustment.compare.marker.new' | translate }}
                </span>
              }
              @if (row.unsupported) {
                <span class="ai-compare__marker ai-compare__marker--warning">
                  {{ 'activity.ai_adjustment.compare.marker.unsupported' | translate }}
                </span>
              }
              @if (row.reordered) {
                <span class="ai-compare__marker">
                  {{ 'activity.ai_adjustment.compare.marker.reordered' | translate }}
                </span>
              }
            </div>
          }
        </div>
      }
    </div>

    <div class="ai-compare__footer">
      @if (state === EAiComparisonState.GENERATING) {
        <span class="ai-compare__footer-generating">
          <mat-spinner diameter="14"></mat-spinner>
          {{ 'activity.ai_adjustment.compare.generating_footer' | translate }}
        </span>
      } @else {
        @if (state === EAiComparisonState.COMPARING) {
          <p class="ai-compare__disclaimer">{{ 'activity.ai_adjustment.compare.disclaimer' | translate }}</p>
        }
        <button mat-stroked-button type="button" [disabled]="discarding" (click)="discard()">
          {{ 'activity.ai_adjustment.compare.discard' | translate }}
        </button>
        <button mat-flat-button color="primary" type="button" [disabled]="discarding" [matMenuTriggerFor]="acceptMenu">
          <mat-icon>check</mat-icon>
          {{ 'activity.ai_adjustment.compare.accept' | translate }}
          <mat-icon>expand_less</mat-icon>
        </button>
        <mat-menu #acceptMenu="matMenu" yPosition="above" xPosition="before">
          <button mat-menu-item type="button" (click)="acceptUpdate()">
            <mat-icon>save</mat-icon>
            {{ 'activity.ai_adjustment.compare.accept_update' | translate }}
          </button>
          <button mat-menu-item type="button" (click)="acceptCopy()">
            <mat-icon>content_copy</mat-icon>
            {{ 'activity.ai_adjustment.compare.accept_copy' | translate }}
          </button>
        </mat-menu>
      }
    </div>
  </div>
}

<ng-template #oldValue let-row="row">
  @if (row.kind === EAiComparisonRowKind.SECTION) {
    @if (row.oldData) {
      <app-ai-adjustment-section-value [data]="row.oldData"></app-ai-adjustment-section-value>
    } @else {
      <p class="ai-compare__absent">{{ 'activity.ai_adjustment.compare.section_absent' | translate }}</p>
    }
  } @else {
    <p class="ai-compare__text">{{ row.oldValue ?? '—' }}</p>
  }
</ng-template>

<ng-template #newValue let-row="row">
  @if (row.kind === EAiComparisonRowKind.SECTION) {
    @if (row.newData) {
      <app-ai-adjustment-section-value [data]="row.newData"></app-ai-adjustment-section-value>
    } @else {
      <p class="ai-compare__absent">{{ 'activity.ai_adjustment.compare.section_removed' | translate }}</p>
    }
  } @else {
    <p class="ai-compare__text">{{ row.newValue ?? '—' }}</p>
  }
</ng-template>
